import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { cn, Spinner } from "@/components/common";

const CATEGORIES = ["Tech", "Home", "Baby & Kids", "Fashion", "Beauty", "Automotive", "Travel", "Fitness", "Other"];
const dateVal = (v) => (v ? String(v).slice(0, 10) : "");

const emptyDeal = () => ({
  title: "", description: "", retailer_id: "", product_url: "", affiliate_url: "",
  image_url: "", category: "Other", price: "", previous_price: "", deal_text: "",
  disclosure: "", start_at: "", expiry_at: "", featured: false, active: true,
});

export default function AdminDeals() {
  const { user } = useAuth();
  const isOwner = ["owner", "admin"].includes(user?.role);
  const [view, setView] = useState("deals");
  const [deals, setDeals] = useState(null);
  const [retailers, setRetailers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [disclosure, setDisclosure] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editRetailer, setEditRetailer] = useState(null);

  const loadDeals = () => api.get("/admin/deals").then(({ data }) => setDeals(data.items)).catch((e) => { setDeals([]); toast.error(apiError(e)); });
  const loadRetailers = () => api.get("/admin/deals/retailers").then(({ data }) => setRetailers(data.items)).catch(() => {});
  const loadAnalytics = () => api.get("/admin/deals/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  const loadDisclosure = () => api.get("/admin/deals/settings/disclosure").then(({ data }) => setDisclosure(data)).catch(() => {});

  useEffect(() => { loadDeals(); loadRetailers(); }, []);
  useEffect(() => { if (view === "analytics") loadAnalytics(); if (view === "settings") loadDisclosure(); }, [view]);

  const saveDeal = async () => {
    try {
      const b = {
        title: editing.title, description: editing.description || null, retailer_id: editing.retailer_id,
        product_url: editing.product_url, affiliate_url: editing.affiliate_url || null,
        image_url: editing.image_url || null, category: editing.category,
        price: editing.price === "" ? null : parseFloat(editing.price),
        previous_price: editing.previous_price === "" ? null : parseFloat(editing.previous_price),
        currency: "aud", deal_text: editing.deal_text || null, disclosure: editing.disclosure || null,
        start_at: editing.start_at || null, expiry_at: editing.expiry_at || null,
        featured: !!editing.featured, active: !!editing.active,
      };
      if (editing.id) await api.put(`/admin/deals/${editing.id}`, b);
      else await api.post("/admin/deals", b);
      toast.success("Deal saved"); setEditing(null); loadDeals();
    } catch (e) { toast.error(apiError(e)); }
  };

  const pauseDeal = async (d) => { try { await api.post(`/admin/deals/${d.id}/pause`, { value: d.active }); loadDeals(); } catch (e) { toast.error(apiError(e)); } };
  const featureDeal = async (d) => { try { await api.post(`/admin/deals/${d.id}/feature`, { value: !d.featured }); loadDeals(); } catch (e) { toast.error(apiError(e)); } };
  const deleteDeal = async (d) => { if (!window.confirm(`Delete "${d.title}"? This cannot be undone.`)) return; try { await api.delete(`/admin/deals/${d.id}`); toast.success("Deleted"); loadDeals(); } catch (e) { toast.error(apiError(e)); } };

  const saveRetailer = async () => {
    try {
      const b = {
        name: editRetailer.name, domain: editRetailer.domain,
        allowed_domains: (editRetailer.allowed_domains_str || "").split(",").map((s) => s.trim()).filter(Boolean),
        logo_url: editRetailer.logo_url || null, disclosure: editRetailer.disclosure || null,
        active: !!editRetailer.active,
      };
      if (editRetailer.id) await api.put(`/admin/deals/retailers/${editRetailer.id}`, b);
      else await api.post("/admin/deals/retailers", b);
      toast.success("Retailer saved"); setEditRetailer(null); loadRetailers();
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteRetailer = async (r) => { if (!window.confirm(`Delete retailer "${r.name}"?`)) return; try { await api.delete(`/admin/deals/retailers/${r.id}`); toast.success("Deleted"); loadRetailers(); } catch (e) { toast.error(apiError(e)); } };

  const saveDisclosure = async () => { try { await api.put("/admin/deals/settings/disclosure", { general_disclosure: disclosure.general_disclosure, amazon_disclosure: disclosure.amazon_disclosure }); toast.success("Disclosures saved"); } catch (e) { toast.error(apiError(e)); } };

  const VIEWS = [["deals", "Deals"], ["retailers", "Retailers"], ["analytics", "Analytics"], ["settings", "Disclosures"]];

  return (
    <div className="space-y-4" data-testid="admin-deals">
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          {VIEWS.map(([id, label]) => (
            <button key={id} onClick={() => setView(id)} data-testid={`deals-view-${id}`}
              className={cn("shrink-0 rounded-full px-3 py-1.5 text-sm font-semibold", view === id ? "bg-primary text-white" : "bg-secondary text-muted-foreground")}>{label}</button>
          ))}
        </div>
        {view === "deals" && isOwner && <Button size="sm" onClick={() => setEditing(emptyDeal())} data-testid="deal-new">New deal</Button>}
        {view === "retailers" && isOwner && <Button size="sm" onClick={() => setEditRetailer({ name: "", domain: "", allowed_domains_str: "", logo_url: "", disclosure: "", active: true })} data-testid="retailer-new">New retailer</Button>}
      </div>

      {view === "deals" && (
        deals === null ? <Spinner /> : deals.length === 0 ? <p className="text-sm text-muted-foreground">No deals yet. Create one to get started.</p> : (
          <div className="space-y-2">
            {deals.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 text-sm" data-testid={`admin-deal-${d.id}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <b className="truncate">{d.title}</b>
                    {d.featured && <span className="rounded bg-brand-green/15 px-1.5 py-0.5 text-[10px] font-bold text-brand-green">FEATURED</span>}
                    {!d.active && <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">PAUSED</span>}
                    {d.is_demo && <span className="rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-bold text-white">DEMO</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">{d.retailer_name} · {d.category} · {d.stats?.impressions || 0} impr · {d.stats?.clicks || 0} clicks · {d.ctr || 0}% CTR</div>
                </div>
                {isOwner && (
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="ghost" onClick={() => featureDeal(d)} data-testid={`deal-feature-${d.id}`}>{d.featured ? "Unfeature" : "Feature"}</Button>
                    <Button size="sm" variant="ghost" onClick={() => pauseDeal(d)} data-testid={`deal-pause-${d.id}`}>{d.active ? "Pause" : "Resume"}</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...d, price: d.price ?? "", previous_price: d.previous_price ?? "", start_at: dateVal(d.start_at), expiry_at: dateVal(d.expiry_at) })} data-testid={`deal-edit-${d.id}`}>Edit</Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteDeal(d)} data-testid={`deal-delete-${d.id}`}>Delete</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {view === "retailers" && (
        <div className="space-y-2">
          {retailers.length === 0 ? <p className="text-sm text-muted-foreground">No retailers yet.</p> : retailers.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-3 text-sm" data-testid={`admin-retailer-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5"><b>{r.name}</b>{!r.active && <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">INACTIVE</span>}{r.is_demo && <span className="rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-bold text-white">DEMO</span>}</div>
                <div className="truncate text-xs text-muted-foreground">{(r.allowed_domains || [r.domain]).join(", ")}</div>
              </div>
              {isOwner && (
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="outline" onClick={() => setEditRetailer({ ...r, allowed_domains_str: (r.allowed_domains || [r.domain]).join(", ") })} data-testid={`retailer-edit-${r.id}`}>Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => deleteRetailer(r)} data-testid={`retailer-delete-${r.id}`}>Delete</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {view === "analytics" && (
        !analytics ? <Spinner /> : (
          <div className="space-y-4" data-testid="deals-analytics">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Total deals" value={analytics.total_deals} />
              <Stat label="Outbound clicks" value={analytics.outbound_clicks} />
              <Stat label="Impressions" value={analytics.total_impressions} />
              <Stat label="Overall CTR" value={`${analytics.total_impressions ? Math.round(analytics.total_clicks / analytics.total_impressions * 100) : 0}%`} />
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <Panel title="Top clicked deals" rows={analytics.top_deals.map((d) => [d.title, d.clicks])} />
              <Panel title="Top retailers" rows={analytics.top_retailers} />
              <Panel title="Top categories" rows={analytics.top_categories} />
            </div>
          </div>
        )
      )}

      {view === "settings" && (
        !disclosure ? <Spinner /> : (
          <div className="space-y-3" data-testid="deals-disclosure-settings">
            <p className="text-sm text-muted-foreground">Affiliate disclosures shown on Mtaa Deals. The general disclosure appears at the top of the Deals page; the Amazon Associates disclosure can be attached to Amazon retailer/deals.</p>
            <div><label className="text-sm font-medium">General affiliate disclosure</label>
              <Textarea value={disclosure.general_disclosure || ""} onChange={(e) => setDisclosure({ ...disclosure, general_disclosure: e.target.value })} data-testid="disclosure-general" /></div>
            <div><label className="text-sm font-medium">Amazon Associates disclosure</label>
              <Textarea value={disclosure.amazon_disclosure || ""} onChange={(e) => setDisclosure({ ...disclosure, amazon_disclosure: e.target.value })} data-testid="disclosure-amazon" /></div>
            {isOwner && <Button onClick={saveDisclosure} data-testid="disclosure-save">Save disclosures</Button>}
          </div>
        )
      )}

      {/* Deal editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="deal-edit-modal">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit deal" : "New deal"}</DialogTitle>
            <DialogDescription>Mtaa never sells these products — links send members to the external retailer. URLs must be on an approved retailer domain.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <Field label="Title"><Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="deal-title" /></Field>
              <Field label="Description"><Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="deal-description" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Retailer">
                  <Select value={editing.retailer_id} onValueChange={(v) => setEditing({ ...editing, retailer_id: v })}>
                    <SelectTrigger data-testid="deal-retailer"><SelectValue placeholder="Select retailer" /></SelectTrigger>
                    <SelectContent>{retailers.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Category">
                  <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="deal-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label="Product URL (retailer page)"><Input value={editing.product_url} onChange={(e) => setEditing({ ...editing, product_url: e.target.value })} placeholder="https://www.amazon.com.au/dp/..." data-testid="deal-product-url" /></Field>
              <Field label="Affiliate URL (optional)"><Input value={editing.affiliate_url} onChange={(e) => setEditing({ ...editing, affiliate_url: e.target.value })} placeholder="https://..." data-testid="deal-affiliate-url" /></Field>
              <Field label="Image URL (optional)"><Input value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} data-testid="deal-image-url" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Current price (AUD)"><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} data-testid="deal-price" /></Field>
                <Field label="Previous price (AUD)"><Input type="number" value={editing.previous_price} onChange={(e) => setEditing({ ...editing, previous_price: e.target.value })} data-testid="deal-prev-price" /></Field>
              </div>
              <Field label="Deal text (optional, e.g. '50% off')"><Input value={editing.deal_text} onChange={(e) => setEditing({ ...editing, deal_text: e.target.value })} data-testid="deal-text" /></Field>
              <Field label="Per-deal disclosure override (optional)"><Input value={editing.disclosure} onChange={(e) => setEditing({ ...editing, disclosure: e.target.value })} data-testid="deal-disclosure" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Start date (optional)"><Input type="date" value={editing.start_at} onChange={(e) => setEditing({ ...editing, start_at: e.target.value })} data-testid="deal-start" /></Field>
                <Field label="Expiry date (optional — evergreen if blank)"><Input type="date" value={editing.expiry_at} onChange={(e) => setEditing({ ...editing, expiry_at: e.target.value })} data-testid="deal-expiry" /></Field>
              </div>
              <label className="flex items-center justify-between text-sm"><span>Featured</span><Switch checked={!!editing.featured} onCheckedChange={(v) => setEditing({ ...editing, featured: v })} data-testid="deal-featured" /></label>
              <label className="flex items-center justify-between text-sm"><span>Active</span><Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="deal-active" /></label>
            </div>
          )}
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="flex-1" onClick={saveDeal} data-testid="deal-save">Save deal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Retailer editor */}
      <Dialog open={!!editRetailer} onOpenChange={(o) => !o && setEditRetailer(null)}>
        <DialogContent data-testid="retailer-edit-modal">
          <DialogHeader><DialogTitle>{editRetailer?.id ? "Edit retailer" : "New retailer"}</DialogTitle>
            <DialogDescription>Only URLs on these approved domains can be used for deals from this retailer.</DialogDescription>
          </DialogHeader>
          {editRetailer && (
            <div className="space-y-3">
              <Field label="Name"><Input value={editRetailer.name} onChange={(e) => setEditRetailer({ ...editRetailer, name: e.target.value })} data-testid="retailer-name" /></Field>
              <Field label="Primary domain"><Input value={editRetailer.domain} onChange={(e) => setEditRetailer({ ...editRetailer, domain: e.target.value })} placeholder="amazon.com.au" data-testid="retailer-domain" /></Field>
              <Field label="Allowed domains (comma-separated, incl. affiliate short domains)"><Input value={editRetailer.allowed_domains_str} onChange={(e) => setEditRetailer({ ...editRetailer, allowed_domains_str: e.target.value })} placeholder="amazon.com.au, amzn.to" data-testid="retailer-domains" /></Field>
              <Field label="Logo URL (optional)"><Input value={editRetailer.logo_url || ""} onChange={(e) => setEditRetailer({ ...editRetailer, logo_url: e.target.value })} data-testid="retailer-logo" /></Field>
              <Field label="Affiliate disclosure (optional)"><Input value={editRetailer.disclosure || ""} onChange={(e) => setEditRetailer({ ...editRetailer, disclosure: e.target.value })} data-testid="retailer-disclosure" /></Field>
              <label className="flex items-center justify-between text-sm"><span>Active</span><Switch checked={!!editRetailer.active} onCheckedChange={(v) => setEditRetailer({ ...editRetailer, active: v })} data-testid="retailer-active" /></label>
            </div>
          )}
          <DialogFooter className="flex-row gap-2">
            <Button variant="outline" className="flex-1" onClick={() => setEditRetailer(null)}>Cancel</Button>
            <Button className="flex-1" onClick={saveRetailer} data-testid="retailer-save">Save retailer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-sm font-medium">{label}</label><div className="mt-1">{children}</div></div>;
}
function Stat({ label, value }) {
  return <div className="rounded-2xl border border-border bg-card p-4"><p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p><p className="mt-1 font-display text-2xl font-extrabold">{value}</p></div>;
}
function Panel({ title, rows }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="font-display font-semibold">{title}</p>
      <div className="mt-2 space-y-1 text-sm">
        {(!rows || rows.length === 0) ? <p className="text-muted-foreground">No data yet.</p> : rows.map((r, i) => <div key={i} className="flex justify-between border-b border-border py-1 last:border-0"><span className="truncate pr-2">{r[0]}</span><span className="font-medium">{r[1]}</span></div>)}
      </div>
    </div>
  );
}
